// Canonical Sill Plus marketing + paywall copy. Shared with the marketing
// `/pricing` surface so feature lists and contextual upgrade messaging stay
// in lockstep. See `docs/superpowers/specs/2026-05-12-sill-plus-design.md`
// §4 — `<UpgradeSheet>` and ADR-026 (multi-surface design system).

// Plus differentiators only (UpgradeReason: `chat_quota`, `custom_alert`).
// Shared-capability rows (calculators, saved properties, manual entry,
// document upload, bank connection & sync, Ask Sill at the free quota) belong
// on the pricing page under "Everything in Free, plus…" — not in this array,
// which is rendered as the Plus-only list on both marketing /pricing and
// SubscriptionSection.
//
// SILL-539: verified bank sync was the LEAD item here and is now a Free
// capability. Do not re-add it, or any rewording of it, to this array — it is
// pinned by pricing-copy.test.ts because a paid-tier claim about bank
// verification contradicts the shipped access policy.
//
// Copy discipline (2026-05-26 launch pass): lead with infrastructure value
// — what Plus *connects*, *verifies*, and *runs the math on*. Do NOT pitch
// marketplace outcomes ("lenders compete for you", "better incentives",
// "thousands off your house"). RESPA Section 8 makes outcome-framed paid
// tiers a hazard for a referral-shaped business; tools-and-data framing
// keeps the value prop on Sill's side of the boundary.
export const PLUS_FEATURES = [
  "Unlimited Ask Sill — no daily message cap",
  "Custom rate alerts — any threshold, daily or weekly cadence",
  "Priority support — questions to the Sill team jump the queue",
] as const;

export type UpgradeReason =
  | "chat_quota"
  | "custom_alert"
  | "rate_cadence_daily"
  | "pricing_page";

// Each entry carries `bullets` as a fixed tuple of exactly three feature
// lines, lead-with-feature: bullet[0] is what the user JUST tried to do, so
// the value-exchange lands first; bullets[1..2] are the other two Plus
// differentiators so the broader value shows even if the lead doesn't land.
// The tuple type prevents 2-or-4-bullet drift at compile time. Style matches
// `PLUS_FEATURES` above so the modal reads as the same product voice.
export const UPGRADE_COPY: Record<
  UpgradeReason,
  {
    title: string;
    subtitle: string;
    bullets: readonly [string, string, string];
  }
> = {
  chat_quota: {
    title: "You've reached today's chat limit",
    subtitle: "Plus removes the daily cap — ask Sill as much as you need.",
    bullets: [
      "Unlimited Ask Sill — no daily message cap",
      "Custom rate alerts — any threshold, daily or weekly cadence",
      "Priority support — questions to the Sill team jump the queue",
    ],
  },
  custom_alert: {
    title: "Watch the market on your terms",
    subtitle:
      "Plus unlocks unlimited rate and payment thresholds, on the cadence you pick.",
    bullets: [
      "Custom rate alerts — any threshold, daily or weekly cadence",
      "Unlimited Ask Sill — no daily message cap",
      "Priority support — questions to the Sill team jump the queue",
    ],
  },
  rate_cadence_daily: {
    title: "Switch your rate-watch to daily updates",
    subtitle: "Plus unlocks daily cadence and unlimited custom thresholds.",
    bullets: [
      "Daily rate alerts — never miss a move that changes your math",
      "Unlimited Ask Sill — no daily message cap",
      "Priority support — questions to the Sill team jump the queue",
    ],
  },
  pricing_page: {
    title: "Try Sill Plus",
    subtitle: "No chat limits, custom rate alerts, priority support.",
    bullets: [
      "Unlimited Ask Sill — no daily message cap",
      "Custom rate alerts — any threshold, daily or weekly cadence",
      "Priority support — questions to the Sill team jump the queue",
    ],
  },
};

// Purchase-paused copy used when SILL_PLUS_PURCHASE_ENABLED=false (backend
// kill-switch — see spec §6.4). Existing subscriptions are unaffected; new
// purchases are deliberately blocked at the UI layer. The flag is exposed
// to Vite builds as `VITE_SILL_PLUS_PURCHASE_ENABLED`; see
// `isPlusPurchaseEnabled()`.
export const PURCHASES_PAUSED_COPY = {
  title: "Plus purchases are temporarily paused",
  subtitle:
    "We're updating Sill Plus. Existing subscribers continue uninterrupted. New purchases will be available again shortly.",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Custom pricing-panel data (replaces Clerk's hosted <PricingTable> embed).
// Both surfaces (consumer modal + marketing /pricing) render this same list.
// ─────────────────────────────────────────────────────────────────────────

export type BillingPeriod = "monthly" | "annual";

export const PLUS_UPGRADE_QUERY_PARAM = "upgrade";
export const PLUS_UPGRADE_QUERY_VALUE = "1";
export const BILLING_PERIOD_QUERY_PARAM = "billing_period";

export function normalizeBillingPeriod(
  value: string | null | undefined,
): BillingPeriod | null {
  return value === "monthly" || value === "annual" ? value : null;
}

export function buildPlusUpgradeQueryParams(
  period: BillingPeriod,
): Record<string, string> {
  return {
    [BILLING_PERIOD_QUERY_PARAM]: period,
    [PLUS_UPGRADE_QUERY_PARAM]: PLUS_UPGRADE_QUERY_VALUE,
  };
}

// Monthly is the rack rate; annual leads with the effective monthly cost
// ($4.99/month) as the headline number, then discloses the actual annual
// charge ($59.88 billed annually) in the supporting meta line. Annual is
// the default selected period (both surfaces) and the billing toggle
// renders a "Save N%" pill on the Annual tab — the percentage is derived
// from these amounts via `plusAnnualSavingsPercent()` so it can never
// drift from the displayed prices ($4.99 vs $9.99 → 50%). Keep amounts as
// strings so display formatting stays a UI concern.
//
// $9.99/month rack rate (2026-05-26 launch pricing); annual stayed at
// $4.99 × 12 = $59.88/year (Clerk Billing only supports annual plans as
// monthly_rate × 12, so the yearly total is a direct consequence of the
// $4.99 monthly equivalent). The annual plan renews yearly; we surface
// that explicitly in the meta line rather than implying a one-time
// payment. CTA uses the headline framing ($4.99/mo) to stay consistent
// with the big number above it.
//
// Trial: configured plan-side in Clerk Billing as 14 days, no card.
// The trial copy lives in the meta lines so anyone changing the Clerk
// plan config can grep for it.
export const PLUS_PRICE = {
  monthly: {
    amount: "9.99",
    periodLabel: "/month",
    metaPrimary: "14-day free trial. No card required to start.",
    metaSecondary: "Billed monthly after trial. Cancel any time.",
    ctaLabel: "Start free trial — then $9.99/mo",
  },
  annual: {
    amount: "4.99",
    periodLabel: "/month",
    metaPrimary: "14-day free trial. No card required to start.",
    metaSecondary: "$59.88 billed annually after trial. Cancel any time.",
    ctaLabel: "Start free trial — then $4.99/mo",
  },
} as const;

// Effective savings of the annual plan vs the monthly rack rate, derived
// from the price amounts above so it can never drift from the displayed
// prices. $4.99 effective vs $9.99 rack → 50%. Rendered as the "Save N%"
// pill on the Annual tab of the billing toggle (marketing + consumer).
// Returns 0 when amounts are unparseable so callers can hide the pill
// rather than render a nonsensical badge.
export function plusAnnualSavingsPercent(): number {
  const monthly = Number(PLUS_PRICE.monthly.amount);
  const annual = Number(PLUS_PRICE.annual.amount);
  if (!Number.isFinite(monthly) || !Number.isFinite(annual) || monthly <= 0) {
    return 0;
  }
  return Math.round((1 - annual / monthly) * 100);
}

// Plan-card feature rows. `kind: "soon"` renders the muted clock icon and the
// "Coming soon" pill. Anything else renders the standard check.
export type PlanFeature = {
  title: string;
  detail: string;
  kind?: "soon";
};

export const FREE_PLAN_FEATURES: PlanFeature[] = [
  {
    title: "Mortgage math & calculators",
    detail:
      "Affordability, payment breakdown, rent vs buy, DPA search.",
  },
  {
    title: "Save and track properties",
    detail:
      "Add homes you're considering and see your numbers on each one.",
  },
  {
    title: "Ask Sill — 20 messages/day",
    detail: "Ask anything about your situation, up to 20 questions a day.",
  },
  {
    title: "1 rate alert",
    detail:
      "Get notified when market rates move. Daily or weekly digest, your pick.",
  },
  {
    title: "Manual profile & document upload",
    detail:
      "Enter income, assets, debts; attach pay stubs, W-2s, statements.",
  },
  {
    title: "Verified bank connection & sync",
    detail:
      "Connect your accounts so every estimate is priced against your real cash and reserves — not a self-reported guess.",
  },
];

export const PLUS_PLAN_FEATURES: PlanFeature[] = [
  {
    title: "Unlimited Ask Sill",
    detail: "No daily message cap. Ask anything, as often as you need it.",
  },
  {
    title: "Custom rate alerts",
    detail:
      "Set rate or payment thresholds — daily or weekly digests, or instant pings on a move that changes your math.",
  },
  {
    title: "Priority support",
    detail:
      "Questions to the Sill team move to the front of the queue.",
  },
];

// Plus plan ID for <CheckoutButton planId=...>. Sourced from the Vite-injected
// env var so each environment (dev/staging/prod) can point at its own Clerk
// plan. Returns null when unset — callers should fall back to a disabled CTA
// rather than handing Clerk an empty string (which would 400 at checkout).
//
// Annual + monthly share a single planId in Clerk Billing; the period is
// passed as a separate `planPeriod` prop on <CheckoutButton>.
export function getPlusPlanId(): string | null {
  const raw = import.meta.env.VITE_CLERK_PLUS_PLAN_ID;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// isPlusPurchaseEnabled returns true unless the build-time env var
// VITE_SILL_PLUS_PURCHASE_ENABLED is explicitly set to "false" or "0".
// Default is true so an unset flag doesn't accidentally hide the pricing
// surface in normal operation. The kill-switch is opt-in by design.
//
// This must be called inside a Vite app (consumer or marketing). Using
// `import.meta.env` is safe in both surfaces because they share the same
// Vite + TypeScript baseline (ADR-026).
export function isPlusPurchaseEnabled(): boolean {
  // Vite replaces `import.meta.env.VITE_*` at build time, so this is a
  // compile-time constant once bundled. To flip the switch, set the env
  // var and rebuild the affected containers (consumer + marketing).
  const raw = import.meta.env.VITE_SILL_PLUS_PURCHASE_ENABLED;
  if (raw === undefined || raw === null || raw === "") return true;
  const normalized = String(raw).trim().toLowerCase();
  return normalized !== "false" && normalized !== "0";
}
